import queryString from "query-string";
import { msalInstance } from "../msalInstance";
import { loginRequest } from "../authConfig";

const API_URL = process.env.REACT_APP_API_URL;

// Get MSAL token securely
async function getAuthHeader(): Promise<Record<string, string>> {
    const account = msalInstance.getActiveAccount();
    if (!account) {
        console.warn("No active account found. User may need to log in.");
        throw new Error("Authentication required");
    }

    try {
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
        });

        if (!response.accessToken) {
            throw new Error("Token acquisition failed");
        }

        return { Authorization: `Bearer ${response.accessToken}` };
    } catch (error) {
        console.error("Silent token acquisition failed:", error);
        throw new Error("Authentication failed");
    }
}

// Generic fetch wrapper with better error handling
async function apiFetch<T>(url: string, options: RequestInit): Promise<T> {
    try {
        const response = await fetch(url, options);

        if (!response.ok) {
            const errorBody = await response.text();
            console.error(`API Error: ${response.status} - ${errorBody}`);
            throw new Error(`API request failed: ${response.statusText}`);
        }

        return response.json() as Promise<T>;
    } catch (error) {
        console.error("Network or parsing error:", error);
        throw error;
    }
}

// Validate simulate_roles before appending
function appendSimulateRoles(url: URL) {
    const { simulate_roles } = queryString.parse(window.location.search);
    if (simulate_roles && typeof simulate_roles === "string") {
        url.searchParams.append("simulate_roles", simulate_roles);
    }
}

// Fetch Menu
export const fetchMenu = async () => {
    const url = new URL(`${API_URL}/menu`);
    appendSimulateRoles(url);

    return apiFetch(url.toString(), {
        mode: "cors",
        credentials: "include",
        headers: await getAuthHeader(),
    });
};

// Fetch Page
export const fetchPage = async (path: string) => {
    const url = new URL(`${API_URL}/page/${encodeURIComponent(path)}`);
    appendSimulateRoles(url);

    return apiFetch(url.toString(), {
        mode: "cors",
        credentials: "include",
        headers: await getAuthHeader(),
    });
};

async function getUserPhotoBase64(): Promise<string | null> {
    const account = msalInstance.getActiveAccount();
    if (!account) return null;

    try {
        const graphTokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ["User.Read"], // Graph scope only
            account,
        });

        const graphResponse = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
            headers: { Authorization: `Bearer ${graphTokenResponse.accessToken}` },
        });

        if (graphResponse.ok) {
            const blob = await graphResponse.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const base64String = btoa(
                new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
            );
            return `data:image/jpeg;base64,${base64String}`;
        }
    } catch (error) {
        console.warn("Could not fetch user photo:", error);
    }

    return null;
}

export const fetchUserImage = async (): Promise<string> => {
    const image = await getUserPhotoBase64();
    return image || "";
};

// ✅ Fetch User with Base64 image
export const fetchUser = async () => {
    const url = new URL(`${API_URL}/user`);

    const response = await apiFetch<any>(url.toString(), {
        mode: "cors",
        method: "GET",
        credentials: "include",
        headers: await getAuthHeader(),
    });

    return response?.firstName ? response : null;
};
